import React, { useMemo } from 'react';
import moment from 'moment';
import { Table } from 'altus-ui-components';
import { Grid, Typography } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import LoopIcon from '@material-ui/icons/Loop';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import { Avatar as TooltipAvatar } from 'app/components/withTooltip';
import {
  ATTACHMENTS_DATE_FORMAT,
  RULE_TYPES_ENUM,
  TRAIL_SOURCE_ENUM,
} from 'features/settings/integrityManagement/helpers/integrityManagement.constants';
import { EMPTY_LIST } from 'app/app.constants';

const IntegrityDetailsHistoryTable = ({ history }) => {
  const formatValue = (ruleDataType, value) => {
    switch (ruleDataType) {
      case RULE_TYPES_ENUM.RULE_BOOL:
        if (value.toLowerCase() === 'true') {
          return 'Yes'
        }
        if (value === undefined) {
          return null;
        }
        return 'No'
      case RULE_TYPES_ENUM.RULE_DOUBLE:
        const number = parseFloat(value);
        return parseFloat(number.toFixed(2));
      default:
        return value;
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'expander',
        disableSortBy: true,
        Cell: ({ row }) => {
          const trail = row.original;

          switch (trail.source) {
            case TRAIL_SOURCE_ENUM.CREATED:
              return (
                <Grid item>
                  <NoteAddIcon />
                </Grid>
              );
            case TRAIL_SOURCE_ENUM.RULE:
              return (
                <Grid item>
                  <LoopIcon />
                </Grid>
              );
            case TRAIL_SOURCE_ENUM.UPLOADED:
            case TRAIL_SOURCE_ENUM.ATTACHED:
            case TRAIL_SOURCE_ENUM.UNATTACHED:
              return (
                <Grid item>
                  <DescriptionIcon />
                </Grid>
              );
            default:
              return null;
          }
        },
      },
      {
        xs: 6,
        Header: 'Rule Change',
        accessor: (history) => history.currentValue,
        Cell: ({ row }) => {
          const trail = row.original;
          
          switch (trail.source) {
            case TRAIL_SOURCE_ENUM.RULE:
              return trail.oldValue ? (
                <Typography>
                  {`Rule Value changed from ${formatValue(trail.ruleDataType, trail.oldValue)} to ${formatValue(trail.ruleDataType, trail.currentValue)}`}
                </Typography>
              ) : (
                <Typography>
                  {`Rule Value changed to ${formatValue(trail.ruleDataType, trail.currentValue)}`}
                </Typography>
              );
            case TRAIL_SOURCE_ENUM.UPLOADED:
              return (
                <Typography>{`${trail.currentValue} was uploaded`}</Typography>
              );
            case TRAIL_SOURCE_ENUM.ATTACHED:
              return (
                <Typography>{`${trail.currentValue} was attached`}</Typography>
              );
            case TRAIL_SOURCE_ENUM.UNATTACHED:
              return (
                <Typography>
                  {`${trail.currentValue} was unattached`}
                </Typography>
              );
            default:
              return (
                <Typography>{`Rule ${trail.currentValue} created.`}</Typography>
              );
          }
        },
      },
      {
        xs: 4,
        Header: 'Done By',
        accessor: (history) => history.user?.fullName,
        Cell: ({ row }) => {
          const user = row.original.user;

          return user ? (
            <Grid container alignItems={'center'}>
              <Grid item xs={2}>
                <TooltipAvatar title={user.fullName} src={user.avatar}>
                  {!user.avatar && user.initials}
                </TooltipAvatar>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">{user.fullName}</Typography>
              </Grid>
            </Grid>
          ) : null;
        },
      },

      {
        xs: 2,
        id: 'date',
        Header: 'Date Changed',
        accessor: (history) =>
          moment(history.dateChanged).format(ATTACHMENTS_DATE_FORMAT),
      },
    ],
    [],
  );

  return (
    <Table
      items={history ?? EMPTY_LIST}
      columns={columns}
      useGlobalFilter={true}
      noItemsMessage="There is no history trail for this rule."
    />
  );
};

export default IntegrityDetailsHistoryTable;
